import {
  Calendar02Icon,
  CodeSquareIcon,
  CreditCardIcon,
  Folder02Icon,
  Notification03Icon,
  Settings01Icon,
  Settings02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon, type HugeiconsIconProps } from '@hugeicons/react';
import { useI18n } from '@pique/i18n';
import { useLiveData, useServices } from '@toeverything/infra';
import { lazy, type FC } from 'react';

import { useIsAdmin } from '../../../../hooks/use-is-admin-or-staff';
import { AuthService, ServerConfigService } from '../../../../modules/cloud';
import type { GeneralSettingKey } from '../types';

const PreferencesSettings = lazy(() =>
  import('./appearance').then(m => ({ default: m.PreferencesSettings }))
);
const IntegrationsSettings = lazy(() =>
  import('./integrations').then(m => ({ default: m.GoogleCalendarSettings }))
);
const NotificationsSettings = lazy(() =>
  import('./notifications/index').then(m => ({
    default: m.NotificationsSettings,
  }))
);
const PiquePricingPlans = lazy(() =>
  import('./plans').then(m => ({ default: m.PiquePricingPlans }))
);
const WorkspaceManagement = lazy(() =>
  import('./workspaces').then(m => ({ default: m.WorkspaceManagement }))
);
const AugmentPromptSettings = lazy(() =>
  import('./augment-prompt').then(m => ({ default: m.AugmentPromptSettings }))
);
const ExperimentalFlagsSettings = lazy(() =>
  import('./preferences').then(m => ({ default: m.ExperimentalFlagsSettings }))
);
const AdminSettings = lazy(() =>
  import('./admin-settings').then(m => ({ default: m.AdminSettings }))
);

interface GeneralSettingListItem {
  key: GeneralSettingKey;
  title: string;
  icon: FC<Omit<HugeiconsIconProps, 'icon'>>;
  testId: string;
  isAdminOnly?: boolean;
  isFeatureFlagged?: boolean;
}

export type GeneralSettingList = GeneralSettingListItem[];

export const useGeneralSettingList = (): GeneralSettingList => {
  const t = useI18n();
  const { authService, serverConfigService } = useServices({
    AuthService,
    ServerConfigService,
  });
  const hasPaymentFeature = useLiveData(
    serverConfigService.serverConfig.features$.map(f => f?.['payment'])
  );
  const featureFlags = useLiveData(
    authService.session.account$.map(
      account => account?.info?.featureFlags ?? []
    )
  );
  const isAdmin = useIsAdmin();
  const canSeeExperimental = Boolean(
    isAdmin || featureFlags?.includes('settingsExperimentalPanel')
  );
  const canSeeDemo = Boolean(
    isAdmin || featureFlags?.includes('settingsDemoPanel')
  );
  const canSeeWorkspaces = Boolean(
    isAdmin || featureFlags?.includes('settingsWorkspacesPanel')
  );

  const settings: GeneralSettingListItem[] = [
    {
      key: 'preferences',
      title: 'Preferences',
      icon: props => <HugeiconsIcon icon={Settings01Icon} {...props} />,
      testId: 'preferences-panel-trigger',
    },
    {
      key: 'integrations',
      title: 'Calendar',
      icon: props => <HugeiconsIcon icon={Calendar02Icon} {...props} />,
      testId: 'integrations-panel-trigger',
    },
    {
      key: 'notifications',
      title: 'Notifications',
      icon: props => <HugeiconsIcon icon={Notification03Icon} {...props} />,
      testId: 'notifications-panel-trigger',
    },
  ];

  if (hasPaymentFeature) {
    settings.push({
      key: 'plans',
      title: t['com.pique.billing.title'](),
      icon: props => <HugeiconsIcon icon={CreditCardIcon} {...props} />,
      testId: 'billing-panel-trigger',
    });
  }

  if (canSeeExperimental) {
    settings.push({
      key: 'experimental-flags',
      title: 'Experimental',
      icon: props => <HugeiconsIcon icon={Settings02Icon} {...props} />,
      testId: 'experimental-flags-panel-trigger',
      isFeatureFlagged: true,
    });
  }
  if (canSeeDemo) {
    settings.push({
      key: 'admin-settings',
      title: 'Demo Features',
      icon: props => <HugeiconsIcon icon={CodeSquareIcon} {...props} />,
      testId: 'admin-settings-panel-trigger',
      isFeatureFlagged: true,
    });
  }
  if (canSeeWorkspaces) {
    settings.push({
      key: 'workspaces',
      title: 'Workspaces',
      icon: props => <HugeiconsIcon icon={Folder02Icon} {...props} />,
      testId: 'workspaces-panel-trigger',
      isFeatureFlagged: true,
    });
  }

  return settings;
};

interface GeneralSettingProps {
  generalKey: GeneralSettingKey;
}

export const GeneralSetting = ({ generalKey }: GeneralSettingProps) => {
  const generalSettings = useGeneralSettingList();
  const isVisible =
    generalKey === 'preferences' ||
    generalKey === 'integrations' ||
    generalKey === 'notifications' ||
    generalKey === 'plans' ||
    generalKey === 'augment-prompt' ||
    generalSettings.some(setting => setting.key === generalKey);

  if (!isVisible) {
    return null;
  }

  switch (generalKey) {
    case 'preferences':
      return <PreferencesSettings />;
    case 'experimental-flags':
      return <ExperimentalFlagsSettings />;
    case 'workspaces':
      return <WorkspaceManagement />;
    case 'plans':
      return <PiquePricingPlans />;
    case 'integrations':
      return <IntegrationsSettings />;
    case 'notifications':
      return <NotificationsSettings />;
    case 'augment-prompt':
      return <AugmentPromptSettings />;
    case 'admin-settings':
      return <AdminSettings />;
    default:
      return null;
  }
};
